/*
 * Bracci: Compre junto (blocks/bracci-pdp-combo.liquid).
 *
 * 1. Busca os complementares do Search & Discovery (intenção "complementary"); sem
 *    nenhum, usa os do metafield custom.combo_itens que o bloco já deixa no HTML.
 *    Para cada complementar do Search & Discovery, busca estoque e pré-venda em
 *    /products/<handle>?view=bracci-combo (templates/product.bracci-combo.liquid).
 * 2. Monta os cartões: complementares, "+" e o produto da página, com o total.
 *    Item em pré-venda ganha a etiqueta, e o aviso abaixo do total mostra a data
 *    mais distante entre os itens (o envio do conjunto é junto).
 * 3. "Adicionar" manda todos os itens ao carrinho de uma vez, cada um com a
 *    propriedade oculta do combo; com código de desconto, aplica no carrinho depois.
 *    O carrinho do Fabric atualiza pelo mesmo evento do bracci-card.js.
 * O bloco é trocado pelo HTML novo a cada troca de acabamento (data-bpdp-refresh),
 * e o connectedCallback monta tudo de novo.
 */

const cache = new Map();

function formatMoney(cents, format) {
  if (window.BracciCard?.formatMoney) return window.BracciCard.formatMoney(cents, format);
  return `R$ ${(cents / 100).toFixed(2).replace('.', ',')}`;
}

function escapeHtml(text) {
  return String(text ?? '').replace(/[&<>"']/g, (char) => `&#${char.charCodeAt(0)};`);
}

function sized(url, width) {
  if (!url) return '';
  try {
    const parsed = new URL(url.startsWith('//') ? `https:${url}` : url, window.location.origin);
    parsed.searchParams.set('width', String(width));
    return parsed.href;
  } catch (error) {
    return url;
  }
}

function readJson(root, selector, fallback) {
  const element = root.querySelector(selector);
  if (!element) return fallback;
  try {
    return JSON.parse(element.textContent);
  } catch (error) {
    return fallback;
  }
}

function fetchComplementary(productId, limit) {
  const key = `${productId}|${limit}`;
  if (cache.has(key)) return cache.get(key);

  const root = window.Shopify?.routes?.root || '/';
  const request = fetch(`${root}recommendations/products.json?product_id=${productId}&limit=${limit + 4}&intent=complementary`)
    .then((response) => (response.ok ? response.json() : { products: [] }))
    .then((data) =>
      (data.products || []).map((product) => {
        const variant = (product.variants || []).find((item) => item.available) || product.variants?.[0];
        return {
          id: product.id,
          title: product.title,
          handle: product.handle,
          url: product.url,
          image: variant?.featured_image?.src || product.featured_image,
          variant: variant?.id,
          price: variant?.price ?? product.price,
          available: Boolean(variant?.available),
        };
      })
    )
    .catch(() => []);
  cache.set(key, request);
  return request;
}

// Estoque e pré-venda de um complementar (templates/product.bracci-combo.liquid)
const details = new Map();

function fetchDetails(product) {
  if (!product.handle) return Promise.resolve(product);
  if (!details.has(product.handle)) {
    const root = window.Shopify?.routes?.root || '/';
    details.set(
      product.handle,
      fetch(`${root}products/${encodeURIComponent(product.handle)}?view=bracci-combo`, { headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.text() : ''))
        .then((text) => JSON.parse(text))
        .catch(() => null)
    );
  }
  return details.get(product.handle).then((data) => (data ? { ...product, ...data, url: product.url || data.url } : product));
}

// Data mais distante (dd/mm/aaaa) entre os textos de pré-venda
function latestDate(texts) {
  let best = null;
  texts.forEach((text) => {
    for (const match of String(text).matchAll(/(\d{1,2})\/(\d{1,2})\/(\d{2,4})/g)) {
      const year = match[3].length === 2 ? 2000 + Number(match[3]) : Number(match[3]);
      const date = new Date(year, Number(match[2]) - 1, Number(match[1]));
      if (!best || date > best.date) best = { date, label: match[0] };
    }
  });
  return best?.label || '';
}

async function refreshCart() {
  const cartItems = document.querySelector('cart-items-component');
  if (cartItems) {
    await customElements.whenDefined('cart-items-component');
    if (typeof cartItems.fetchCartData === 'function') return cartItems.fetchCartData();
  }
  const cartUrl = window.Theme?.routes?.cart_url || '/cart';
  const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  return response.json();
}

class BracciCombo extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.button = this.querySelector('[data-bcombo-add]');
    this.label = this.querySelector('[data-bcombo-label]');
    this.button?.addEventListener('click', () => this.add());
    this.load();
  }

  get discount() {
    return Math.min(Math.max(Number(this.dataset.discount) || 0, 0), 90);
  }

  discounted(cents) {
    return this.discount > 0 ? Math.round((cents * (100 - this.discount)) / 100) : cents;
  }

  async load() {
    const current = readJson(this, '[data-bcombo-current]', null);
    if (!current) return;

    const limit = Number(this.dataset.limit) || 2;
    const source = this.dataset.source || 'complementary_then_metafield';
    let list = [];

    if (source !== 'metafield') {
      list = await fetchComplementary(this.dataset.productId, limit);
      list = list.filter((product) => product.available && product.variant && String(product.id) !== String(current.id));
      // Só os que vão aparecer: estoque e pré-venda de cada um
      list = await Promise.all(list.slice(0, limit + 2).map(fetchDetails));
      if (!this.isConnected) return;
    }
    list = list.filter((product) => product.available && product.variant && String(product.id) !== String(current.id));
    if (!list.length && source !== 'complementary') list = readJson(this, '[data-bcombo-fallback]', []);

    this.items = list.slice(0, limit);
    this.current = current;

    const hint = this.nextElementSibling?.matches('[data-bcombo-hint]') ? this.nextElementSibling : null;
    if (!this.items.length) {
      this.hidden = true;
      if (hint) hint.hidden = false;
      return;
    }
    if (hint) hint.hidden = true;
    this.render();
    this.hidden = false;
  }

  card(product) {
    const price = Number(product.price) || 0;
    const shown = this.discounted(price);
    const format = this.dataset.moneyFormat;
    const image = product.image
      ? `<img src="${escapeHtml(sized(product.image, 160))}" alt="" width="64" height="64" loading="lazy">`
      : '';
    const tags = [];
    if (product === this.current && this.dataset.currentLabel) tags.push(this.dataset.currentLabel);
    if (product.state === 'presale' && this.dataset.presaleTag) tags.push(this.dataset.presaleTag);
    const tag = tags.length ? `<span class="bcombo__tag">${tags.map(escapeHtml).join(' · ')}</span>` : '';
    return `<div class="bcombo__item">
        <a class="bcombo__image" href="${escapeHtml(product.url)}" tabindex="-1" aria-hidden="true">${image}</a>
        <span class="bcombo__info">
          ${tag}
          <a class="bcombo__name" href="${escapeHtml(product.url)}">${escapeHtml(product.title)}</a>
        </span>
        <span class="bcombo__prices">
          <span class="bcombo__price">${formatMoney(shown, format)}</span>
          ${shown < price ? `<s class="bcombo__compare">${formatMoney(price, format)}</s>` : ''}
        </span>
      </div>`;
  }

  render() {
    const all = [...this.items, this.current];
    const list = this.querySelector('[data-bcombo-list]');
    list.innerHTML = all.map((product) => this.card(product)).join('<span class="bcombo__plus" aria-hidden="true"><span>+</span></span>');

    const format = this.dataset.moneyFormat;
    const full = all.reduce((sum, product) => sum + (Number(product.price) || 0), 0);
    const shown = all.reduce((sum, product) => sum + this.discounted(Number(product.price) || 0), 0);
    this.querySelector('[data-bcombo-total]').innerHTML =
      (shown < full ? `<s class="bcombo__compare">${formatMoney(full, format)}</s> ` : '') +
      `<strong>${formatMoney(shown, format)}</strong>`;

    // Aviso de pré-venda: a data mais distante entre os itens, já que o envio é junto
    const notice = this.querySelector('[data-bcombo-presale]');
    const presale = all.filter((product) => product.state === 'presale');
    const message = this.dataset.presaleMessage;
    if (notice) {
      if (presale.length && message) {
        const texts = presale.map((product) => product.presale_text).filter(Boolean);
        const date = latestDate(texts);
        // Sem data no texto do metafield, mostra o próprio texto
        notice.textContent = date ? message.replace('[data]', date) : texts[0] || '';
        notice.hidden = !notice.textContent;
      } else {
        notice.hidden = true;
      }
    }

    const available = this.current.available;
    this.button.disabled = !available;
    this.label.textContent = available ? this.dataset.labelAdd : this.dataset.labelSoldout;
  }

  lines() {
    const property = this.dataset.comboProperty;
    const comboId = [...this.items, this.current].map((product) => product.id).join('+');

    return [...this.items, this.current].map((product) => {
      const line = { id: Number(product.variant), quantity: 1, properties: {} };
      if (property) line.properties[property] = comboId;
      // Item em pré-venda: plano do app ou a propriedade de pré-venda
      if (product.selling_plan) {
        line.selling_plan = Number(product.selling_plan);
      } else if (product.presale_note && this.dataset.presaleName) {
        line.properties[this.dataset.presaleName] = product.presale_note;
      }
      if (!Object.keys(line.properties).length) delete line.properties;
      return line;
    });
  }

  flash(state, text, duration) {
    this.button.classList.remove('is-loading');
    this.button.classList.add(state);
    this.label.textContent = text;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.button.classList.remove(state);
      this.label.textContent = this.dataset.labelAdd;
    }, duration);
  }

  async add() {
    if (!this.items?.length || this.button.disabled || this.button.classList.contains('is-loading')) return;
    this.button.classList.remove('is-added', 'is-error');
    this.button.classList.add('is-loading');

    let events = null;
    try {
      events = await import('@shopify/events');
    } catch (error) {
      /* sem importmap: o carrinho só atualiza ao recarregar */
    }

    const items = this.lines();
    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);

    const deferred = events?.CartLinesUpdateEvent.createPromise();
    if (events && deferred) {
      this.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: items.map((line) => ({ merchandiseId: String(line.id), quantity: 1 })),
          promise: deferred.promise,
        })
      );
    }

    const routes = window.Theme?.routes || {};
    const detailBase = { source: 'bracci-combo', sourceId: this.dataset.blockId, itemCount: items.length, productId: this.dataset.productId };

    try {
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      }).then((result) => result.json());

      if (response.status) {
        if (deferred) {
          refreshCart()
            .then((cart) =>
              deferred.resolve({
                cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                detail: { ...detailBase, items: cart.items, didError: true },
              })
            )
            .catch(deferred.reject);
        }
        this.flash('is-error', response.description || this.dataset.labelError, 2600);
        return;
      }

      // Código de desconto do combo (opcional), aplicado ao carrinho
      const code = this.dataset.discountCode;
      if (code) {
        await fetch(routes.cart_update_url || '/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: code }),
        }).catch(() => {});
      }

      if (deferred) {
        const cart = await refreshCart();
        deferred.resolve({
          cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detailBase, items: cart.items, sections: response.sections, didError: false },
        });
      }
      this.flash('is-added', this.dataset.labelAdded, 1600);
    } catch (error) {
      console.error('[bracci-combo]', error);
      deferred?.reject(error);
      this.flash('is-error', this.dataset.labelError, 2600);
    }
  }
}

if (!customElements.get('bracci-combo')) customElements.define('bracci-combo', BracciCombo);
